import Button from "../Button";
import OutlineButton from "../Button/OutlineButton";
import { commaSeperatedNumberFormat } from "src/lib/utils/format-text";

type Props = {
  requiresDeposit: boolean;
  /** Share of the offer that must be deposited up front, e.g. "20". */
  percentage: string | number;
  currency: string;
  balance: string | number;
  onFundWallet: () => void;
  fundLoading?: boolean;
  submitLabel: string;
  onSubmit: () => void;
  submitLoading?: boolean;
  submitDisabled?: boolean;
};

/**
 * The foot of an offer form: the deposit the client still owes, if any, and
 * the button that sends the offer.
 *
 * Fixed-cost and retainer offers each carried their own copy of this, with a
 * grey hairline box, the amount in an outlined span mid-sentence and a navy
 * submit. It follows the balance bar on Project Details instead: the amount
 * as a surface pill, one line of explanation, and the orange commit button.
 */
const OfferActions = ({
  requiresDeposit,
  percentage,
  currency,
  balance,
  onFundWallet,
  fundLoading,
  submitLabel,
  onSubmit,
  submitLoading,
  submitDisabled,
}: Props) => (
  <div
    className={
      requiresDeposit
        ? "flex flex-col justify-between gap-4 rounded-xl border p-4 md:flex-row md:items-center"
        : "flex justify-end"
    }
  >
    {requiresDeposit && (
      <div className="flex flex-col items-start gap-3 md:flex-row md:items-center">
        <div className="whitespace-nowrap rounded-lg bg-surface px-4 py-2 text-lg font-bold tabular-nums text-brand-navy">
          {currency} {commaSeperatedNumberFormat(balance)}
        </div>
        <p className="text-xs text-muted-foreground">
          <span className="font-semibold text-brand-navy">
            {percentage}% initial deposit required.
          </span>{" "}
          Top up your wallet with this amount; it is held safely in escrow.
        </p>
      </div>
    )}

    <div className="flex shrink-0 justify-end gap-3">
      {requiresDeposit && (
        <OutlineButton
          label="Fund Wallet"
          loading={fundLoading}
          className={undefined}
          onClick={onFundWallet}
        />
      )}
      <Button
        label={submitLabel}
        loading={submitLoading}
        disabled={submitDisabled || submitLoading}
        onClick={onSubmit}
      />
    </div>
  </div>
);

export default OfferActions;
